import { useState } from 'react';
import { PenLine } from 'lucide-react';
import nacl from 'tweetnacl';
import { Button } from '@/components/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/base/card';
import { Textarea } from '@/components/base/textarea';
import { Label } from '@/components/base/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/base/alert';
import { ed25519SignWithScalar, bytesToHex } from '@/localSign';
import { useAccount } from '@/state/account';

/**
 * Sign an arbitrary UTF-8 message and display the resulting signature.
 *
 * Use case: a user with staked tokens (or any other state that can't be
 * unlocked with a plain transfer) constructs the unstake/withdraw transaction
 * with a CLI tool and uses this to produce a signature they can attach.
 */
export function SignMessageCard() {
  const { account, sigResults, pushSig } = useAccount();
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (!account) return null;

  const sign = (text: string) => {
    setError(null);
    try {
      const msgBytes = new TextEncoder().encode(text);
      const sig = ed25519SignWithScalar(account.scalar, account.pubkey, msgBytes);
      const verified = nacl.sign.detached.verify(msgBytes, sig, account.pubkey);
      pushSig({ message: text, signatureHex: bytesToHex(sig), verified });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to sign message.');
    }
  };

  const latest = sigResults[0] ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="para:flex para:items-center para:gap-2">
          <PenLine className="para:size-5" />
          Sign message
        </CardTitle>
        <CardDescription>
          Produces a 64-byte Ed25519 signature (returned as hex). Useful for advanced flows like signing an
          externally-constructed unstake transaction.
        </CardDescription>
      </CardHeader>
      <CardContent className="para:flex para:flex-col para:gap-3">
        <div className="para:flex para:flex-col para:gap-2">
          <Label htmlFor="sign-message">Message (UTF-8)</Label>
          <Textarea
            id="sign-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Message to sign"
            rows={4}
          />
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Signing failed</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {latest && (
          <Alert variant={latest.verified ? 'default' : 'destructive'}>
            <AlertTitle>{latest.verified ? 'Signature' : 'Verification failed'}</AlertTitle>
            <AlertDescription>
              <code className="para:font-mono para:text-xs para:break-all">0x{latest.signatureHex}</code>
            </AlertDescription>
          </Alert>
        )}
        <Button onClick={() => sign(message)} disabled={!message}>
          Sign
        </Button>

        {sigResults.length > 1 && (
          <details className="para:mt-2">
            <summary className="para:cursor-pointer para:text-xs para:font-medium para:text-muted-foreground para:hover:text-foreground">
              Earlier signatures ({sigResults.length - 1})
            </summary>
            <div className="para:mt-3 para:flex para:flex-col para:gap-3">
              {sigResults.slice(1).map((r, i) => (
                <div
                  key={i}
                  className="para:flex para:flex-col para:gap-1 para:rounded-md para:border para:border-border para:p-3"
                >
                  <span className="para:text-xs para:text-muted-foreground">message</span>
                  <code className="para:font-mono para:text-xs para:break-all">{r.message || '(empty)'}</code>
                  <span className="para:mt-1 para:text-xs para:text-muted-foreground">signature (hex)</span>
                  <code className="para:font-mono para:text-xs para:break-all">{r.signatureHex}</code>
                  <span className="para:mt-1 para:text-xs">
                    {r.verified ? (
                      <span className="para:text-emerald-600 para:font-medium">✓ verified</span>
                    ) : (
                      <span className="para:text-destructive para:font-medium">✗ verification failed</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
